/**
 * Per-zone Talk feed buffers. One MessageStore + one unread counter per zone id.
 * Stores are isolated (a message that matches two overlapping zones lands in both,
 * deduped within each). Unread bumps only for messages arriving into a zone that is
 * not the one currently being viewed.
 */
import { MessageStore, type MessageEntry } from "./message-model.js";

export class ZoneFeeds {
  #stores = new Map<string, MessageStore>();
  #unread = new Map<string, number>();

  store(zoneId: string): MessageStore {
    let s = this.#stores.get(zoneId);
    if (!s) { s = new MessageStore(); this.#stores.set(zoneId, s); }
    return s;
  }

  unread(zoneId: string): number {
    return this.#unread.get(zoneId) ?? 0;
  }

  /** Add to the zone's store; bump its unread when it's new and not the active zone. */
  ingest(zoneId: string, entry: MessageEntry, activeZoneId: string): boolean {
    const added = this.store(zoneId).add(entry);
    if (added && zoneId !== activeZoneId) {
      this.#unread.set(zoneId, this.unread(zoneId) + 1);
    }
    return added;
  }

  markSeen(zoneId: string): void {
    this.#unread.set(zoneId, 0);
  }

  reconcile(zoneIds: string[]): void {
    const keep = new Set(zoneIds);
    for (const id of [...this.#stores.keys()]) if (!keep.has(id)) this.#stores.delete(id);
    for (const id of [...this.#unread.keys()]) if (!keep.has(id)) this.#unread.delete(id);
  }
}
